import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserCircle, FaBuilding, FaUser, FaChevronDown, FaSpinner } from 'react-icons/fa';
import './DevUserSwitcher.css';

const DevUserSwitcher = ({ onUserSwitch, currentUserId }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState({ individuals: [], corporates: [] });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch('/api/dev/users');
        if (response.ok) {
          const data = await response.json();
          setUsers(data);
        }
      } catch (err) {
        console.error('Failed to fetch dev users:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const currentUser = [...users.individuals, ...users.corporates].find(u => u.uid === currentUserId);

  const handleSelect = async (user) => {
    if (!user) {
      localStorage.removeItem('authToken');
      onUserSwitch(null);
      setIsOpen(false);
      navigate('/');
      window.location.reload();
      return;
    }
    
    setSwitching(true);
    try {
      const response = await fetch('/api/dev/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid })
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('authToken', data.token);
        onUserSwitch(user);
        setIsOpen(false);
        
        const isCorporate = user.role === 'subscriber' || user.subscriptionType;
        const targetPath = isCorporate ? '/corporate-dashboard' : '/individual-dashboard';
        navigate(targetPath);
        window.location.reload();
      } else {
        console.error('Failed to switch user');
      }
    } catch (err) {
      console.error('Failed to switch user:', err);
    } finally {
      setSwitching(false);
    }
  };

  if (loading) return null;

  return (
    <div className="dev-user-switcher">
      <div className="switcher-trigger" onClick={() => setIsOpen(!isOpen)}>
        <FaUserCircle className="trigger-icon" />
        <span className="trigger-text">
          {currentUser ? currentUser.displayName : 'Select Test User'}
        </span>
        <FaChevronDown className={`chevron ${isOpen ? 'open' : ''}`} />
      </div>

      {isOpen && (
        <div className="switcher-dropdown">
          <div className="dropdown-section">
            <div className="section-header">
              <FaUser /> Individual Users ({users.individuals.length})
            </div>
            {users.individuals.map(user => (
              <div 
                key={user.uid}
                className={`user-option ${currentUserId === user.uid ? 'active' : ''}`}
                onClick={() => handleSelect(user)}
              >
                <span className="user-name">{user.displayName}</span>
                <span className="user-reports">{user.reportCount} reports</span>
              </div>
            ))}
          </div>

          <div className="dropdown-section">
            <div className="section-header">
              <FaBuilding /> Corporate Users ({users.corporates.length})
            </div>
            {users.corporates.map(user => (
              <div 
                key={user.uid}
                className={`user-option corporate ${currentUserId === user.uid ? 'active' : ''}`}
                onClick={() => handleSelect(user)}
              >
                <span className="user-name">{user.displayName}</span>
                <span className="user-type">{user.subscriptionType}</span>
              </div>
            ))}
          </div>

          <div className="dropdown-footer">
            <button onClick={() => { onUserSwitch(null); setIsOpen(false); }}>
              Clear Selection
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevUserSwitcher;
